import { motion, useMotionValueEvent } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHeroScroll } from "../../context/HeroScrollContext";
import { useLenis } from "../../context/SmoothScrollContext";
import { useViewport } from "../../context/ViewportContext";
import { SECTIONS } from "../../lib/constants";
import { heroColorTransform } from "../../lib/utils";

interface RailStackProps {
  sections?: Array<{ id: string; label: string }>;
  darkSectionIds?: Set<string> | string[];
}

/**
 * Animated rails that reflect section scroll position.
 */
export function RailStack({ sections = SECTIONS, darkSectionIds }: RailStackProps) {
  const { vh } = useViewport();
  const numRails = sections.length;
  
  const [barColors, setBarColors] = useState<string[]>([]);
  const [scrollY, setScrollY] = useState(0);
  const lastYRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const [sectionBounds, setSectionBounds] = useState<Array<{ start: number; end: number }>>([]);
  const nodesRef = useRef<HTMLElement[]>([]);
  const lenis = useLenis();
  const { heroProgress } = useHeroScroll();
  const [heroValue, setHeroValue] = useState(0);

  useMotionValueEvent(heroProgress, "change", (v) => {
    setHeroValue(typeof v === "number" ? v : 0);
  });

  const heroColor = useMemo(() => heroColorTransform(heroValue), [heroValue]);

  useEffect(() => {
    const schedule = (y: number) => {
      lastYRef.current = y;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          setScrollY(lastYRef.current);
        });
      }
    };

    // Prefer Lenis scroll updates if available
    if (lenis) {
      const handler = ({ scroll }: { scroll: number }) => schedule(scroll);
      // Prime initial value
      schedule(lenis.scroll);
      lenis.on("scroll", handler as any);
      return () => {
        lenis.off("scroll", handler as any);
      };
    }

    // Fallback to native scroll
    const onScroll = () => schedule(window.scrollY || window.pageYOffset);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lenis]);

  // Measure all snap sections to support variable heights (e.g., Manifesto > 1vh)
  useEffect(() => {
    const getDocumentOffsetTop = (el: HTMLElement | null) => {
      let top = 0;
      let node: HTMLElement | null = el;
      while (node) {
        top += node.offsetTop || 0;
        node = node.offsetParent as HTMLElement | null;
      }
      return top;
    };
    const measure = () => {
      const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-snap-section]"));
      if (!nodes.length) {
        setSectionBounds([]);
        return;
      }
      nodesRef.current = nodes;
      const bounds = nodes.map((el) => {
        const start = getDocumentOffsetTop(el);
        const height = Math.max(el.offsetHeight, vh || el.offsetHeight);
        const end = start + height;
        return { start, end };
      });
      setSectionBounds(bounds);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [vh]);

  // Compute section and intra directly from measured bounds and scroll position
  const { section, intra } = useMemo(() => {
      if (!sectionBounds.length) {
      const sectionFloat = vh > 0 ? scrollY / vh : 0;
      return {
        section: Math.max(0, Math.min(numRails - 1, Math.floor(sectionFloat))),
        intra: Math.min(1, Math.max(0, sectionFloat - Math.floor(sectionFloat))),
      };
      }
    let idx = 0;
    for (let i = 0; i < sectionBounds.length; i += 1) {
      if (scrollY >= sectionBounds[i].start) idx = i;
      else break;
      }
    idx = Math.max(0, Math.min(numRails - 1, idx));
    const b = sectionBounds[idx];
    const t = b ? (scrollY - b.start) / Math.max(1, b.end - b.start) : 0;
    return { section: idx, intra: Math.min(1, Math.max(0, t)) };
  }, [scrollY, vh, numRails, sectionBounds]);

  const topY = 8;
  const bottomBase = Math.max(0, vh - 8);
  const spacing = 20;
  const railHeight = 10;

  const isMobile = typeof window !== "undefined" ? window.innerWidth < 640 : false;
  const dpr = typeof window !== "undefined" ? Math.max(1, Math.min(3, Math.round(window.devicePixelRatio || 1))) : 2;
  const snapToDpr = (v: number) => Math.round(v * dpr) / dpr;
  const heroBlendSections = useMemo(() => new Set(["HERO", "INDEX"]), []);

  const sectionForY = (yViewport: number) => {
    const docY = scrollY + yViewport;
    const matchIdx = sectionBounds.findIndex(
      (b) => docY >= b.start && docY <= b.end,
    );
    return matchIdx >= 0 ? sections[matchIdx]?.id : sections[section]?.id;
  };

  const rotationFor = (y: number) => {      
      if (y <= topY + 2) return -45; 
      return -45 + (y / vh) * 170;
  };

  const yFor = (index: number) => {
    if (index < section - 1) return snapToDpr(-railHeight - 8);

    if (index === section - 1) {
      const t = Math.max(0, (intra - 0.5) / 0.5);
      return snapToDpr(topY - t * (railHeight + 8));
    }

    const futureCount = Math.max(0, numRails - 1 - section);
    const movingStart = bottomBase - futureCount * spacing;

    if (index === section) {
      if (section === 0) return snapToDpr(topY);
      const delay = 0.15; // Wait for 15% of the section
      const delayedIntra = Math.max(0, (intra - delay) / (1 - delay));
      return snapToDpr(movingStart - delayedIntra * (movingStart - topY));
    }

    const offset = index - section;
    return snapToDpr(movingStart + offset * spacing);
  };

  const scaleFor = (y: number) => {
    // Normalized position: 0 (top) to 1 (bottom)
    const t = Math.max(0, Math.min(1, y / vh));    
    const peak = 4 * t * (1 - t);
    const minScale = 1.5;
    const maxScale = 10;
    return minScale + (maxScale - minScale) * peak;
  };

  return (
    <div className="hidden md:block fixed inset-0 pointer-events-none z-20" aria-hidden>
      {Array.from({ length: numRails - 1 }).map((_, index) => (
        <motion.div
          key={index}
          className={`fixed left-0 right-0 top-0 w-[calc(100%-56px)] mx-auto z-25 rounded-[1px] pointer-events-none transform-gpu will-change-transform`}
          initial={false}
          style={{
            height: railHeight,
            y: yFor(index),
            rotateX: isMobile
              ? 0
              : rotationFor(yFor(index)),
            scaleY: isMobile ? 1 : scaleFor(yFor(index)), // Apply dynamic scaleY
            transformPerspective: isMobile ? 0 : 250,
            backgroundColor: (() => {
              const activeId =
                sections[section]?.id || sectionForY(yFor(index)) || "";
              if (heroBlendSections.has(activeId)) return heroColor as any;
              return barColors[index] || "rgba(36,32,33,1)";
            })(),
          }}
        />
      ))}
    </div>
  );
}
